/**
 * Import preview domain, frontend mirror of
 * backend/src/services/chat_agent/import_flow.py::latest_import_preview_for_session.
 *
 * The review sheet is not stored client-side (transcript-as-state, spec §4.5):
 * it is a projection of chat messages' `metadata`. Tolerant coercion (drop,
 * never throw) and the replay rule are kept semantics-identical to the backend
 * so both sides project the same sheet from the same transcript — except the
 * snapshot-level validation, which is per-ROW here: a bad row is dropped while
 * the rest of the sheet stays usable (the backend's pydantic
 * model_validate is all-or-nothing; the save endpoint re-validates the final
 * sheet state server-side, so the display-side tolerance is safe).
 */
import type { ChatMessage } from './chatApi';
import type { ImportPreview, ImportPreviewRow } from './chatStream';

export type { ImportPreview, ImportPreviewRow };

// The only keys an import_edit patch may touch — the user shapes the row, the
// code owns its verdict. Mirrors EDITABLE_ROW_FIELDS in import_flow.py.
const EDITABLE_ROW_FIELDS: ReadonlySet<string> = new Set([
  'symbol',
  'quantity',
  'price',
  'currency',
  'date',
  'instrument_id',
  'resolution_default',
]);

const KINDS = ['position', 'trade'] as const;
const SUGGESTIONS = ['new', 'update', 'match', 'needs_edit', 'dupe'] as const;
const RESOLUTIONS = ['add', 'replace'] as const;
const SUMMARY_KEYS = [
  'positions_new',
  'positions_update',
  'matches',
  'needs_edit',
  'trades_new',
  'trades_dupes',
] as const;

function isDict(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function asStr(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

function asNum(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function inEnum<T extends string>(v: unknown, values: readonly T[]): T | null {
  return typeof v === 'string' && (values as readonly string[]).includes(v) ? (v as T) : null;
}

/** Tolerant row coercion. A row without a usable identity (row_id / kind /
 * suggestion) has no consumer downstream (render, edit, save) and is dropped;
 * every other field degrades to null on a type mismatch. */
function coerceRow(raw: unknown): ImportPreviewRow | null {
  if (!isDict(raw)) return null;
  const rowId = asStr(raw.row_id);
  const kind = inEnum(raw.kind, KINDS);
  const suggestion = inEnum(raw.suggestion, SUGGESTIONS);
  if (!rowId || kind === null || suggestion === null) return null;
  return {
    row_id: rowId,
    kind,
    suggestion,
    symbol: asStr(raw.symbol),
    instrument_id: asNum(raw.instrument_id),
    name: asStr(raw.name),
    currency: asStr(raw.currency),
    quantity: asNum(raw.quantity),
    price: asNum(raw.price),
    date: asStr(raw.date),
    confidence: asNum(raw.confidence),
    existing_quantity: asNum(raw.existing_quantity),
    qty_delta: asNum(raw.qty_delta),
    reason: asStr(raw.reason),
    resolution_default: inEnum(raw.resolution_default, RESOLUTIONS),
  };
}

/** Tolerant snapshot coercion. group_id + a rows array gate usability (same
 * fields the stream layer guards on) — without them the sheet has no consumer
 * and the snapshot is treated as corrupt. */
function coercePreview(raw: unknown): ImportPreview | null {
  if (!isDict(raw)) return null;
  const groupId = asNum(raw.group_id);
  if (groupId === null || !Array.isArray(raw.rows)) return null;
  const rows: ImportPreviewRow[] = [];
  for (const item of raw.rows) {
    const coerced = coerceRow(item);
    if (coerced !== null) rows.push(coerced); // corrupt row: dropped, the rest stay usable
  }
  const importIds = Array.isArray(raw.import_ids)
    ? raw.import_ids.filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
    : [];
  const summarySrc = isDict(raw.summary) ? raw.summary : {};
  const summary = {
    positions_new: 0,
    positions_update: 0,
    matches: 0,
    needs_edit: 0,
    trades_new: 0,
    trades_dupes: 0,
  };
  for (const key of SUMMARY_KEYS) {
    const value = asNum(summarySrc[key]);
    if (value !== null) summary[key] = value;
  }
  return { import_ids: importIds, group_id: groupId, rows, summary };
}

/** Apply one edit patch to its row, verbatim (twin of the backend replay's
 * setattr: the edits endpoint validated every key AND value before the
 * message was persisted, so folding re-validates nothing). */
function foldEdit(preview: ImportPreview, edit: unknown): void {
  if (!isDict(edit)) return;
  const patch = edit.patch;
  if (!isDict(patch)) return;
  for (const target of preview.rows) {
    if (target.row_id !== edit.row_id) continue; // unknown row_id: dropped
    const writable = target as unknown as Record<string, unknown>;
    for (const key of Object.keys(patch)) {
      if (EDITABLE_ROW_FIELDS.has(key)) writable[key] = patch[key];
    }
  }
}

/** Full session replay (spec §4.5, twin of the backend replay): the projected
 * sheet plus which lifecycle state the transcript ends in. Scan runs
 * newest-first (`messages` arrive in ChatConversation order, oldest first).
 * A terminal receipt (import_saved / import_undone / import_discarded) newer
 * than any snapshot sets the state and stops — preview is null in all three
 * (the bar renders its saved state from the receipt itself). An edit alone
 * never resumes a closed draft; a NEW snapshot after a receipt re-arms it.
 * Unlike the thesis replay, a receipt check runs BEFORE the edit check (a
 * meta carrying both is a receipt); when one meta pathologically carries
 * several receipts the colder state wins (undo/discarded outrank saved). */
export function replayImportSession(messages: ChatMessage[]): {
  preview: ImportPreview | null;
  state: 'none' | 'active' | 'saved' | 'undone' | 'discarded';
} {
  const edits: unknown[] = [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const meta = messages[i]?.metadata;
    if (!isDict(meta)) continue;
    if ('import_undone' in meta) return { preview: null, state: 'undone' };
    if ('import_discarded' in meta) return { preview: null, state: 'discarded' };
    if ('import_saved' in meta) return { preview: null, state: 'saved' };
    const edit = meta.import_edit;
    if (isDict(edit)) {
      edits.push(edit);
      continue;
    }
    const preview = coercePreview(meta.import_preview);
    if (preview !== null) {
      for (let j = edits.length - 1; j >= 0; j--) {
        foldEdit(preview, edits[j]); // oldest→newest
      }
      return { preview, state: 'active' };
    }
    // corrupt/absent snapshot: keep scanning — an older one stays active
  }
  return { preview: null, state: 'none' };
}

/** One unsaved import draft of the chip-layer projection (T16). `pos` is the
 * winning snapshot's index in `messages` — the recency key the consumer
 * orders entries (and merges cross-kind entries) by. */
export interface ImportDraftEntry {
  preview: ImportPreview;
  pos: number;
}

/** Chip-layer replay (chat-centric revamp T16): the session's unsaved import
 * draft as a LIST, so the return-chips consumer can treat thesis and import
 * drafts uniformly. Import is keyed on nothing — ONE draft per session — so
 * the list is always length 0|1 and this is the single-slot
 * {@link replayImportSession} with the active preview surfaced as an entry.
 * The scan is duplicated (rather than delegated) because replayImportSession
 * must keep returning its terminal STATE for the bar, and its return shape is
 * asserted exact in tests. Semantics here must stay its twin:
 * newest-first, receipt checks before the edit check, edits fold
 * oldest→newest, corrupt snapshots skipped. */
export function replayImportEntries(messages: ChatMessage[]): ImportDraftEntry[] {
  const edits: unknown[] = [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const meta = messages[i]?.metadata;
    if (!isDict(meta)) continue;
    if ('import_undone' in meta || 'import_discarded' in meta || 'import_saved' in meta) {
      return []; // terminal receipt newer than any snapshot: the draft is closed
    }
    const edit = meta.import_edit;
    if (isDict(edit)) {
      edits.push(edit);
      continue;
    }
    const preview = coercePreview(meta.import_preview);
    if (preview !== null) {
      for (let j = edits.length - 1; j >= 0; j--) {
        foldEdit(preview, edits[j]); // oldest→newest
      }
      return [{ preview, pos: i }];
    }
    // corrupt/absent snapshot: keep scanning — an older one stays active
  }
  return [];
}

/** Rows the Apply button commits: `new` + `update` (update only ever occurs
 * on positions; match/dupe/needs_edit commit nothing). Counts the folded
 * rows, NOT summary — the replay fold mutates rows, never the summary. */
export function applyCount(preview: ImportPreview): number {
  return preview.rows.filter((r) => r.suggestion === 'new' || r.suggestion === 'update').length;
}

/** Rows the sheet flags for the user: suggestion `needs_edit`. */
export function flaggedCount(preview: ImportPreview): number {
  return preview.rows.filter((r) => r.suggestion === 'needs_edit').length;
}
